'use client'

import { useMemo, useRef, useState } from 'react'
import type { FipavStanding } from '@/lib/fipav/scraper'
import type { PoolMatch } from '@/lib/services/competitions-hub'
import { computeStandingsAsOf } from '@/lib/standings'
import { StandingsFullTable } from '@/components/portal/StandingsFullTable'
import { TeamTrendChart } from '@/components/portal/TeamTrendChart'

/** Quanto può arrivare stretta ciascuna delle due box (percentuale). */
const MIN_PCT = 30
const MAX_PCT = 70

/**
 * Blocco "classifica + andamento" del match center.
 *
 * Le due card condividono lo stato della **squadra selezionata**: un click su una
 * riga della classifica aggiunge quella squadra al grafico, così l'andamento si
 * legge in confronto con le due contendenti.
 *
 * La larghezza delle due box è regolabile trascinando il **manipolo centrale**
 * (visibile da `lg` in su, dove le box stanno affiancate). Il manipolo è anche
 * un controllo da tastiera: `role="separator"` con frecce ←/→ (⇧ per passi più
 * larghi) e `Home` per tornare al 50%. Sotto `lg` le box sono impilate e il
 * manipolo non viene mostrato.
 *
 * È un client component perché tiene questi stati; la pagina resta un server
 * component.
 */
export function MatchStandingsExplorer({
  standings,
  allMatches,
  homeTeam,
  awayTeam,
  poolSlug,
  poolCode,
  upToGiornata = null,
}: {
  standings: FipavStanding[]
  allMatches: PoolMatch[]
  homeTeam: string
  awayTeam: string
  poolSlug: string
  poolCode: string
  /**
   * Giornata della partita che si sta guardando: classifica e andamento si
   * fermano qui (le giornate successive, se giocate, restano tratteggiate nel
   * grafico). `null` = nessun limite, si mostra lo stato corrente.
   */
  upToGiornata?: number | null
}) {
  const [selectedTeam, setSelectedTeam] = useState<string | null>(null)
  const [leftPct, setLeftPct] = useState(50)
  const [dragging, setDragging] = useState(false)
  // Cambia a fine ridimensionamento: ricrea il grafico con il contenitore ormai
  // stabile (vedi `resizeKey` in TeamTrendChart).
  const [chartResizeKey, setChartResizeKey] = useState(0)
  const containerRef = useRef<HTMLDivElement | null>(null)

  // Classifica **alla giornata della partita**: punti e record si ricostruiscono
  // dalle partite fino a lì, così una gara della 15ª non mostra i risultati
  // delle giornate successive.
  const scopedStandings = useMemo(
    () => computeStandingsAsOf(standings, allMatches, upToGiornata),
    [standings, allMatches, upToGiornata],
  )
  const scopedMatches = useMemo(
    () => (upToGiornata == null ? allMatches : allMatches.filter((m) => m.giornata <= upToGiornata)),
    [allMatches, upToGiornata],
  )

  /** Aggiorna la divisione e chiede al grafico di riagganciarsi alla nuova larghezza. */
  const applySplit = (next: number | ((p: number) => number)) => {
    setLeftPct(next)
    setChartResizeKey((k) => k + 1)
  }

  const applyFromClientX = (clientX: number) => {
    const rect = containerRef.current?.getBoundingClientRect()
    if (!rect || rect.width === 0) return
    const pct = ((clientX - rect.left) / rect.width) * 100
    setLeftPct(Math.min(MAX_PCT, Math.max(MIN_PCT, pct)))
  }

  const onHandlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
    setDragging(true)
    applyFromClientX(event.clientX)
  }

  const onHandlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging) return
    event.preventDefault()
    applyFromClientX(event.clientX)
  }

  const onHandlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging) return
    setDragging(false)
    // Il grafico si riaggancia solo ora, non a ogni frame del trascinamento.
    setChartResizeKey((k) => k + 1)
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
  }

  const onHandleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? 10 : 4
    if (event.key === 'ArrowLeft') {
      event.preventDefault()
      applySplit((p) => Math.max(MIN_PCT, p - step))
    } else if (event.key === 'ArrowRight') {
      event.preventDefault()
      applySplit((p) => Math.min(MAX_PCT, p + step))
    } else if (event.key === 'Home') {
      event.preventDefault()
      applySplit(50)
    }
  }

  return (
    <div
      ref={containerRef}
      style={{ ['--left-pct' as string]: `${leftPct}%` }}
      className={`mt-10 flex flex-col gap-6 lg:flex-row lg:gap-0 ${dragging ? 'select-none' : ''}`}
    >
      <div className="flex min-w-0 flex-col lg:w-[var(--left-pct)] lg:shrink-0">
        <p className="league-kicker">
          Classifica {poolCode}
          {upToGiornata != null && (
            <span className="ml-1.5 font-normal normal-case tracking-normal text-[color:var(--league-muted)]">
              · dopo la {upToGiornata}ª giornata
            </span>
          )}
        </p>
        <div className="league-card-elevated mt-4 w-full overflow-x-auto rounded-2xl p-4">
          <StandingsFullTable
            standings={scopedStandings}
            allMatches={scopedMatches}
            highlightTeams={[homeTeam, awayTeam]}
            selectedTeam={selectedTeam}
            onSelectTeam={setSelectedTeam}
          />
        </div>
      </div>

      {/* Manipolo di ridimensionamento: separatore trascinabile fra le due box. */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Ridimensiona classifica e andamento"
        aria-valuenow={Math.round(leftPct)}
        aria-valuemin={MIN_PCT}
        aria-valuemax={MAX_PCT}
        tabIndex={0}
        onPointerDown={onHandlePointerDown}
        onPointerMove={onHandlePointerMove}
        onPointerUp={onHandlePointerUp}
        onPointerCancel={onHandlePointerUp}
        onKeyDown={onHandleKeyDown}
        onDoubleClick={() => applySplit(50)}
        title="Trascina per ridimensionare · doppio click (o Home) per tornare al 50%"
        className="hidden w-6 shrink-0 cursor-col-resize touch-none items-center justify-center rounded-full outline-none transition-colors hover:bg-[color:var(--league-surface-alt)] focus-visible:bg-[color:var(--league-surface-alt)] focus-visible:ring-2 focus-visible:ring-[color:var(--league-brand)] lg:flex"
      >
        <span
          aria-hidden
          className={`h-10 w-1 rounded-full transition-colors ${
            dragging ? 'bg-[color:var(--league-brand-solid)]' : 'bg-[color:var(--league-line-strong)]'
          }`}
        />
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-4">
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <p className="league-kicker">Andamento</p>
          <span className="text-[12px] text-[color:var(--league-muted)]">
            {selectedTeam
              ? `Confronto con ${selectedTeam}`
              : 'Tocca una squadra in classifica per confrontarla'}
          </span>
        </div>
        {/* La card si stira fino a fondo colonna: la riga prende l'altezza della
            classifica, quindi grafico e tabella finiscono alla stessa quota.
            `min-h` evita un grafico schiacciato quando la classifica ha poche
            squadre. */}
        <div className="league-card-elevated flex flex-1 flex-col rounded-2xl p-4">
          <div className="h-[300px] lg:h-auto lg:min-h-[300px] lg:flex-1">
            <TeamTrendChart
              homeTeam={homeTeam}
              awayTeam={awayTeam}
              extraTeam={selectedTeam}
              allMatches={allMatches}
              poolSlug={poolSlug}
              upToGiornata={upToGiornata}
              resizeKey={chartResizeKey}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
